import { Button } from '@contember/bindx-ui'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import { type ReactNode, useEffect, useSyncExternalStore } from 'react'

export interface ConfirmDialogRequest {
	title: ReactNode
	description?: ReactNode
	confirmLabel?: string
	cancelLabel?: string
	tone?: 'default' | 'danger' | 'warning'
}

interface PendingConfirmDialog {
	request: ConfirmDialogRequest
	resolve: (confirmed: boolean) => void
}

const listeners = new Set<() => void>()
let pendingConfirmDialog: PendingConfirmDialog | null = null

const subscribe = (listener: () => void): () => void => {
	listeners.add(listener)
	return () => listeners.delete(listener)
}

const getSnapshot = (): PendingConfirmDialog | null => pendingConfirmDialog

const emitChange = (): void => {
	for (const listener of listeners) listener()
}

const settle = (pending: PendingConfirmDialog, confirmed: boolean): void => {
	if (pendingConfirmDialog !== pending) return
	pendingConfirmDialog = null
	pending.resolve(confirmed)
	emitChange()
}

/** Opens the shared confirmation dialog and resolves with the user's choice. */
export function confirmDialog(request: ConfirmDialogRequest): Promise<boolean> {
	if (pendingConfirmDialog) pendingConfirmDialog.resolve(false)

	return new Promise(resolve => {
		pendingConfirmDialog = { request, resolve }
		emitChange()
	})
}

/** Single application-level host for imperative confirmation requests. */
export function ConfirmDialogHost() {
	const pending = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

	useEffect(() => {
		return () => {
			if (pendingConfirmDialog) settle(pendingConfirmDialog, false)
		}
	}, [])

	if (!pending) return null

	const { request } = pending
	const isDanger = request.tone === 'danger'

	return (
		<ConfirmDialog
			actions={
				<>
					<ConfirmDialogCancel>
						<Button className="btn-ghost" type="button" variant="ghost">{request.cancelLabel ?? 'Zrušit'}</Button>
					</ConfirmDialogCancel>
					<AlertDialogPrimitive.Action asChild>
						<Button
							className={isDanger
								? 'btn-ghost border-danger text-danger hover:bg-danger-bg hover:text-danger'
								: 'btn-orange'}
							onClick={() => settle(pending, true)}
							type="button"
							variant={isDanger ? 'ghost' : undefined}
						>
							{request.confirmLabel ?? 'Potvrdit'}
						</Button>
					</AlertDialogPrimitive.Action>
				</>
			}
			description={request.description}
			onOpenChange={open => {
				if (!open) settle(pending, false)
			}}
			open
			title={request.title}
		/>
	)
}

/**
 * Centred modal for decisions the user must answer before anything else can
 * happen. Built on Radix AlertDialog (focus trap, Escape → cancel, no
 * dismiss-on-outside-click) rather than the right-side `EditSheet`, which is a
 * form drawer and can be dismissed by accident.
 *
 * The caller owns the buttons so a dialog can offer more than yes/no; wire the
 * cancelling one through `onOpenChange(false)` or `AlertDialogCancelAction`.
 *
 * One accent per dialog: exactly one filled `btn-orange` for the action we
 * recommend (or a red outline when the recommended action destroys data),
 * everything else quiet. Pass `variant="ghost"` on those quiet ones — bindx's
 * default variant paints `bg-primary`, a utility that outranks our
 * components-layer `.btn-ghost` and turns every button salmon.
 */
export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	actions,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	title: ReactNode
	description?: ReactNode
	actions: ReactNode
}) {
	return (
		<AlertDialogPrimitive.Root onOpenChange={onOpenChange} open={open}>
			<AlertDialogPrimitive.Portal>
				<AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out data-[state=open]:fade-in" />
				<AlertDialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-light-gray bg-white shadow-lg data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out data-[state=open]:fade-in">
					<div className="px-6 py-5">
						<AlertDialogPrimitive.Title className="font-vag-normal text-xl leading-snug text-blue-primary">
							{title}
						</AlertDialogPrimitive.Title>
						{description
							? (
								<AlertDialogPrimitive.Description className="mt-2 text-sm leading-relaxed text-gray">
									{description}
								</AlertDialogPrimitive.Description>
							)
							: null}
					</div>
					<div className="flex flex-wrap items-center justify-end gap-2 border-t border-light-gray px-6 py-4">
						{actions}
					</div>
				</AlertDialogPrimitive.Content>
			</AlertDialogPrimitive.Portal>
		</AlertDialogPrimitive.Root>
	)
}

/** Wrap the cancelling button so Radix restores focus and closes the dialog. */
export function ConfirmDialogCancel({ children }: { children: ReactNode }) {
	return <AlertDialogPrimitive.Cancel asChild>{children}</AlertDialogPrimitive.Cancel>
}
